/**
 * User Service
 * Handles all user-related API calls
 */

import apiClient from '../client';
import { ENDPOINTS } from '../endpoints';

/**
 * Fetch user info by wallet address
 * @param {string} userAddress - User wallet address
 * @returns {Promise<Object>} - User info object
 */
export const getUserByAddress = async (userAddress) => {
  if (!userAddress) {
    throw new Error('User address is required');
  }
  const url = `/user/${userAddress}/info`;
  const response = await apiClient.get(url);
  // Handle response - could be object with user property or direct user object
  return response.data || response.user || response;
};

/**
 * Fetch user info by user ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - User info object
 */
export const getUserInfo = async (userId) => {
  const response = await apiClient.get(ENDPOINTS.USER.INFO, {
    body: { userId },
  });
  return response;
};

/**
 * Fetch current user's profile
 * @returns {Promise<Object>} - User profile object
 */
export const getProfile = async () => {
  const response = await apiClient.get(ENDPOINTS.USER.PROFILE);
  return response;
};

/**
 * Update user profile
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<Object>} - Updated profile
 */
export const updateProfile = async (profileData) => {
  const response = await apiClient.put(ENDPOINTS.USER.UPDATE_PROFILE, profileData);
  return response;
};

/**
 * Fetch user score and growth data by wallet address
 * @param {string} userAddress - User wallet address
 * @returns {Promise<Object>} - Score growth data including graph points
 */
export const getUserScoreGrowth = async (userAddress) => {
  if (!userAddress) {
    throw new Error('User address is required');
  }
  const url = `/user/${userAddress}/score/growth`;
  const response = await apiClient.get(url);
  // Handle response - could be object with data property or direct data object
  return response.data || response;
};

const userService = {
  getUserByAddress,
  getUserInfo,
  getProfile,
  updateProfile,
  getUserScoreGrowth,
};

export default userService;
